// =============================================================================
// PricingConfig — UI-editable per-unit cost rates (singleton)
// =============================================================================
// Holds the cost rates that providers do NOT auto-report and must be set by
// hand from the Test Lab "Preise" panel:
//   - LLM per-1M-token rates (OpenRouter dev / Azure prod)
//   - Cartesia TTS (per 1M characters)
//   - Deepgram STT (per minute)
//   - Twilio telephony (per minute)
//   - USD→EUR conversion
//
// Stored as one JSON blob (`dataJSON`) — mirrors TestLabBoard exactly, so the
// resolver/UI plumbing is reused. Read + cached by cost-tracking.ts; UI saves
// invalidate that cache. Rates resolve: DB (this) → env → hardcoded default.
// =============================================================================

import type { Maybe } from '@adornis/base/utilTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import type { PartialEntityData } from '@adornis/baseql/entities/types.js';

@Entity()
export class PricingConfig extends MongoEntity {
  static override _class = 'PricingConfig';
  static override _collectionName = 'pricing_config';

  constructor(data: PartialEntityData<PricingConfig>) {
    super(data);
  }

  /** Fixed key for the singleton config document. */
  @Field(() => String) configKey: Maybe<string>;
  /** JSON-stringified rates: { llm:{openrouter,azure:{inPerM,outPerM}}, cartesia:{usdPer1MChars}, deepgram:{usdPerMin}, twilio:{usdPerMin}, usdToEur }. */
  @Field(() => String) dataJSON: Maybe<string>;
  /** When the rates were last saved. */
  @Field(() => Date) updatedAt: Maybe<Date>;
  /** Auth userID of last editor (audit). */
  @Field(() => String) updatedBy: Maybe<string>;
}
